<template>
  <v-sheet
    class="rounded-xxl pa-5 pa-md-10 ga-5 d-flex flex-column elevation-1 w-100"
    min-width="350"
  >
    <h3>{{ t("components.onboarding.tasksSummary.title") }}</h3>

    <div v-if="!onboardingActions" class="d-flex justify-center align-center flex-column ga-10">
      <v-progress-circular indeterminate color="black" size="100" />
      <p v-if="countdown >= 0" class="text-caption text-grey">
        {{ t("components.onboarding.tasksSummary.redirect") }} {{ countdown }}
        {{ t("components.onboarding.tasksSummary.seconds") }}
      </p>
      <p v-else class="text-caption text-grey">
        {{ t("components.onboarding.tasksSummary.notRedirected") }}
        <a class="text-decoration-underline cursor-pointer" @click="redirect">{{
          t("components.onboarding.tasksSummary.clickHere")
        }}</a>
      </p>
    </div>

    <div v-else class="d-flex flex-column ga-5">
      <div class="d-flex flex-column ga-3">
        <div
          v-for="{ name, text, status, color, hasCTA } in actions"
          :key="name"
          class="d-flex justify-space-between align-center"
        >
          <span :class="hasCTA ? 'text-body-1' : 'text-body-2 text-grey'">{{ text }}</span>

          <v-chip
            :color="color"
            variant="flat"
            class="d-flex justify-center font-weight-medium status-pill"
            :disabled="!hasCTA"
            @click="emit('onboardingStatusClick', name)"
          >
            {{ status }}
          </v-chip>
        </div>
      </div>

      <p v-if="!isAllTasksDone" class="text-caption text-grey">
        {{ t("components.onboarding.tasksSummary.amendDetails") }}
      </p>
    </div>
  </v-sheet>
</template>

<script lang="ts" setup>
import { OnboardingActions, OnboardingActionStatus } from "@tableyeti/merchant-service";
import { onMounted, computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useMerchantsStore } from "@/store/merchants";
import { useOnboardingStore } from "@/store/onboarding";

const onboardingStore = useOnboardingStore();
const merchantsStore = useMerchantsStore();
const i18n = useI18n();

const { onboardingActions } = defineProps<{ onboardingActions?: OnboardingActions }>();
const isLegacyCustomer = computed(
  () => (merchantsStore.currentMerchant?.createdAt ?? "") < "2024-07-17",
);
const actions = computed(() =>
  Object.entries(onboardingActions || {})
    .map(([key, value]) => ({
      name: key,
      status: getStatusText(value),
      text: getTaskText(key),
      color: getColor(value),
      hasCTA: shouldBeClickable(value),
    }))
    .filter((action) => action.name !== "contract" || !isLegacyCustomer.value),
);
const emit = defineEmits<{ onboardingStatusClick: [taskName: string] }>();

const countdown = ref(3);
onMounted(() => {
  const interval = setInterval(() => {
    countdown.value--;
    if (countdown.value === 0) {
      window.open(onboardingStore.draftMerchant?.hostedOnboardingLink, "_self");
    } else if (countdown.value < 0) {
      clearInterval(interval);
    }
  }, 1000);
});

const redirect = async () => {
  await onboardingStore.submitDraftMerchant();
  window.open(onboardingStore.draftMerchant?.hostedOnboardingLink, "_self");
};

const getColor = (status: OnboardingActionStatus | undefined) => {
  switch (status) {
    case OnboardingActionStatus.Done:
      return "green";
    case OnboardingActionStatus.InProgress:
      return "purple";
    case OnboardingActionStatus.NeedsReview:
      return "warning";
    case OnboardingActionStatus.NotStarted:
      return "warning";
    default:
      return "purple";
  }
};

const getTaskText = (taskName: string = "") => {
  return taskName
    .replace(/([A-Z])/g, " $1")
    .replace("pci", "PCI")
    .replace(/^./, (char) => char.toUpperCase());
};

const getStatusText = (status: string | undefined) => {
  switch (status) {
    case OnboardingActionStatus.Done:
      return "Done";
    case OnboardingActionStatus.InProgress:
      return "In progress";
    case OnboardingActionStatus.NeedsReview:
      return "Needs review";
    case OnboardingActionStatus.NotStarted:
      return "Not started";
    default:
      return "In progress";
  }
};

const shouldBeClickable = (status: OnboardingActionStatus | undefined) => {
  return (
    status === OnboardingActionStatus.NeedsReview || status === OnboardingActionStatus.NotStarted
  );
};

const isAllTasksDone = computed(() => {
  return (
    onboardingActions &&
    Object.values(onboardingActions).every((status) => status === OnboardingActionStatus.Done)
  );
});

const t = (key: string) => {
  return i18n.t(key);
};
</script>

<style scoped>
.status-pill {
  width: 140px;
}
</style>
